import { elapsedMs, stopClock, touchClock } from './clock.ts';
import { advanceIndex, ECHO_WINDOW_MS, placeBuffer, startSession } from './entry.ts';
import { rankNextAnswers } from './navigation/navigation.ts';
import type {
	AnswerStatus,
	ArrowKey,
	Cursor,
	EntrySession,
	GameEvent,
	GameState,
	ModelAnswer,
	PuzzleModel,
	SolveSource,
	Transition,
} from './types.ts';

const LETTER = /^[A-Z]$/;

/**
 * Ends any smart-entry session (and its echo): every non-typing action does. Same object
 * if there's none.
 */
function endEntry(state: GameState): GameState {
	if (state.entry === undefined && state.echo === undefined) return state;
	const { entry: _entry, echo: _echo, ...rest } = state;
	return rest;
}

function same(state: GameState): Transition {
	return { state, events: [] };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Cells belonging to a solved answer. They are correct and can never be edited. */
export function lockedCells(model: PuzzleModel, state: GameState): Set<number> {
	const set = new Set<number>();
	for (const id of state.solvedAnswers) for (const cell of model.answers[id]?.cells ?? []) set.add(cell);
	return set;
}

export function isCellLocked(model: PuzzleModel, state: GameState, cell: number): boolean {
	return state.solvedAnswers.some((id) => model.answers[id]?.cells.includes(cell) ?? false);
}

/** The flat cell index under the cursor, or null. */
export function cursorCell(model: PuzzleModel, state: GameState): number | null {
	if (!state.cursor) return null;
	return model.answers[state.cursor.answerId]?.cells[state.cursor.index] ?? null;
}

export function isComplete(state: GameState): boolean {
	return state.completedAt !== null;
}

export function answerStatus(model: PuzzleModel, state: GameState, answerId: string): AnswerStatus {
	if (state.solvedAnswers.includes(answerId)) return 'solved';
	const answer = model.answers[answerId];
	if (!answer) return 'empty';
	const filled = answer.cells.filter((c) => state.entries[c] !== '').length;
	if (filled === 0) return 'empty';
	return filled === answer.cells.length ? 'full' : 'partial';
}

function isCorrect(model: PuzzleModel, entries: string[], answer: ModelAnswer): boolean {
	return answer.cells.every((c) => entries[c] !== '' && entries[c] === model.solution[c]);
}

function boardCorrect(model: PuzzleModel, entries: string[]): boolean {
	return model.letterCells.every((c) => entries[c] === model.solution[c]);
}

// ---------------------------------------------------------------------------
// Creation & restore
// ---------------------------------------------------------------------------

/**
 * A fresh attempt. The cursor starts as null; the UI selects the first answer
 * (`selectAnswer(model, state, model.answerOrder[0])`) or waits for a tap.
 */
export function createGame(model: PuzzleModel): GameState {
	return {
		puzzleId: model.puzzle.id,
		entries: Array.from({ length: model.size }, () => ''),
		cursor: null,
		solvedAnswers: [],
		flaggedCells: [],
		checksUsed: 0,
		revealsUsed: 0,
		startedAt: null,
		completedAt: null,
		elapsedMs: 0,
		runningSince: null,
	};
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isTime = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isTimeOrNull = (v: unknown): v is number | null => v === null || isTime(v);

/**
 * Defensive parse of a persisted `GameState`. Returns null for anything malformed or
 * belonging to another puzzle; never throws.
 *
 * Derived data is re-derived rather than trusted: `solvedAnswers` is recomputed from the
 * entries, flags on empty/locked cells are dropped, and completion is re-checked. The
 * clock comes back **paused** (`runningSince: null`): time while the page was closed
 * must not count. The UI calls `resume(state, now)` once the page is visible (typing
 * also resumes it).
 */
export function restoreGame(model: PuzzleModel, raw: unknown): GameState | null {
	try {
		if (!isObj(raw)) return null;
		if (raw.puzzleId !== model.puzzle.id) return null;

		const entries = raw.entries;
		if (!Array.isArray(entries) || entries.length !== model.size) return null;
		for (let i = 0; i < entries.length; i++) {
			const e = entries[i];
			if (typeof e !== 'string') return null;
			if (e === '') continue;
			if (!LETTER.test(e) || model.kinds[i] !== 'letter') return null;
		}
		const cleanEntries = entries as string[];

		if (!isCount(raw.checksUsed) || !isCount(raw.revealsUsed)) return null;
		if (raw.mistakes !== undefined && !isCount(raw.mistakes)) return null;
		if (!isTimeOrNull(raw.startedAt) || !isTimeOrNull(raw.completedAt) || !isTimeOrNull(raw.runningSince)) return null;
		if (!isTime(raw.elapsedMs) || raw.elapsedMs < 0) return null;

		let cursor: Cursor | null = null;
		if (raw.cursor !== null && raw.cursor !== undefined) {
			const c = raw.cursor;
			if (!isObj(c) || typeof c.answerId !== 'string' || !isCount(c.index)) return null;
			const answer = model.answers[c.answerId];
			if (!answer || c.index >= answer.cells.length) return null;
			cursor = { answerId: c.answerId, index: c.index };
		}

		if (!Array.isArray(raw.solvedAnswers) || !raw.solvedAnswers.every((id) => typeof id === 'string')) return null;
		if (!Array.isArray(raw.flaggedCells) || !raw.flaggedCells.every(isCount)) return null;

		// Re-derive solved answers: keep the stored order for ones that really are correct,
		// then add any correct ones that were missing, in answer order.
		const solved: string[] = [];
		for (const id of raw.solvedAnswers as string[]) {
			const answer = model.answers[id];
			if (answer && !solved.includes(id) && isCorrect(model, cleanEntries, answer)) solved.push(id);
		}
		for (const id of model.answerOrder) {
			if (!solved.includes(id) && isCorrect(model, cleanEntries, model.answers[id]!)) solved.push(id);
		}

		const extras = restoreSolveRecord(model, raw, solved.length);
		const state: GameState = {
			puzzleId: model.puzzle.id,
			entries: [...cleanEntries],
			cursor,
			solvedAnswers: solved,
			flaggedCells: [],
			checksUsed: raw.checksUsed,
			revealsUsed: raw.revealsUsed,
			...(raw.mistakes !== undefined ? { mistakes: raw.mistakes as number } : {}),
			...extras,
			startedAt: raw.startedAt,
			completedAt: raw.completedAt,
			elapsedMs: raw.elapsedMs,
			runningSince: null,
		};
		const locked = lockedCells(model, state);
		state.flaggedCells = [...new Set(raw.flaggedCells as number[])]
			.filter((c) => c < model.size && cleanEntries[c] !== '' && !locked.has(c))
			.sort((a, b) => a - b);

		const correct = boardCorrect(model, cleanEntries);
		if (!correct) state.completedAt = null;
		else if (state.completedAt === null) state.completedAt = (state.startedAt ?? 0) + state.elapsedMs;
		if (state.completedAt !== null && state.startedAt === null) state.startedAt = state.completedAt;
		return state;
	} catch {
		return null;
	}
}

/**
 * The solve record (`revealedAnswers`, `everFlagged`, `solveOffsets`) from a persisted
 * state. These are history, not derivable from the entries, and older saves lack them,
 * so a malformed one is dropped on its own (never the whole state). Unknown answers,
 * non-letter cells and duplicates are dropped; offsets must be finite, non-negative and
 * ascending, and are capped at the number of solved answers.
 */
function restoreSolveRecord(
	model: PuzzleModel,
	raw: Record<string, unknown>,
	solvedCount: number,
): Pick<GameState, 'revealedAnswers' | 'everFlagged' | 'solveOffsets'> {
	const out: Pick<GameState, 'revealedAnswers' | 'everFlagged' | 'solveOffsets'> = {};
	const revealed = raw.revealedAnswers;
	if (Array.isArray(revealed) && revealed.every((id) => typeof id === 'string')) {
		out.revealedAnswers = [...new Set(revealed as string[])].filter((id) => model.answers[id] !== undefined);
	}
	const flagged = raw.everFlagged;
	if (Array.isArray(flagged) && flagged.every(isCount)) {
		out.everFlagged = [...new Set(flagged as number[])]
			.filter((c) => c < model.size && model.kinds[c] === 'letter')
			.sort((a, b) => a - b);
	}
	const offsets = raw.solveOffsets;
	if (
		Array.isArray(offsets) &&
		offsets.every((t, i) => isTime(t) && t >= 0 && (i === 0 || t >= (offsets[i - 1] as number)))
	) {
		out.solveOffsets = (offsets as number[]).slice(0, solvedCount);
	}
	return out;
}

// ---------------------------------------------------------------------------
// Selection & navigation
// ---------------------------------------------------------------------------

function firstEmptyIndex(answer: ModelAnswer, entries: string[]): number {
	const i = answer.cells.findIndex((c) => entries[c] === '');
	return i === -1 ? 0 : i;
}

/** Selects an answer. The default index is its first empty cell (0 when full). */
export function selectAnswer(model: PuzzleModel, state: GameState, answerId: string, index?: number): GameState {
	const answer = model.answers[answerId];
	if (!answer || answer.cells.length === 0) return state;
	const i =
		index === undefined || !Number.isFinite(index)
			? firstEmptyIndex(answer, state.entries)
			: Math.min(Math.max(0, Math.trunc(index)), answer.cells.length - 1);
	return endEntry({ ...state, cursor: { answerId, index: i } });
}

function at(model: PuzzleModel, answerId: string, cell: number): Cursor {
	return { answerId, index: model.answers[answerId]!.cells.indexOf(cell) };
}

/**
 * A tap on a cell (flat index).
 *
 * - Letter cell: tapping the cursor cell again toggles to the crossing answer; a cell in
 *   the current answer moves the index; otherwise the answer on the current axis (else
 *   the other) is selected. With no cursor, across is preferred.
 * - Clue cell: selects that clue's answer (first empty cell). Tapping a two-clue cell
 *   whose answer is already selected switches to the other clue.
 * - Blank cell: no change.
 */
export function selectCell(model: PuzzleModel, state: GameState, cell: number): GameState {
	const kind = model.kinds[cell];
	const cur = state.cursor;

	if (kind === 'clue') {
		const ids = (model.cluesByCell[cell] ?? [])
			.map((clueId) => model.clues[clueId]?.answerId)
			.filter((id): id is string => id !== undefined && model.answers[id] !== undefined);
		if (ids.length === 0) return state;
		const curPos = cur ? ids.indexOf(cur.answerId) : -1;
		const target = curPos !== -1 && ids.length > 1 ? ids[(curPos + 1) % ids.length]! : ids[0]!;
		return selectAnswer(model, state, target);
	}
	if (kind !== 'letter') return state;

	const covering = model.answersByCell[cell] ?? [];
	if (covering.length === 0) return state;

	if (cur) {
		const curAnswer = model.answers[cur.answerId]!;
		if (curAnswer.cells[cur.index] === cell) {
			const other = covering.find((id) => id !== cur.answerId);
			return other ? endEntry({ ...state, cursor: at(model, other, cell) }) : state;
		}
		if (curAnswer.cells.includes(cell)) return endEntry({ ...state, cursor: at(model, cur.answerId, cell) });
	}
	const axis = cur ? model.answers[cur.answerId]!.axis : 'across';
	const pick = covering.find((id) => model.answers[id]!.axis === axis) ?? covering[0]!;
	return endEntry({ ...state, cursor: at(model, pick, cell) });
}

const STEP: Record<ArrowKey, [number, number]> = {
	left: [0, -1],
	right: [0, 1],
	up: [-1, 0],
	down: [1, 0],
};

/**
 * Arrow keys. An arrow perpendicular to the current answer first switches to the
 * crossing answer at the cursor cell, if there is one. Otherwise it moves to the
 * nearest letter cell in that direction (jumping over clue/blank cells), choosing the
 * answer on the arrow's axis there. At the grid edge nothing changes. With no cursor,
 * the first answer is selected.
 */
export function moveCursor(model: PuzzleModel, state: GameState, dir: ArrowKey): GameState {
	const cur = state.cursor;
	if (!cur) {
		const first = model.answerOrder[0];
		return first ? selectAnswer(model, state, first) : state;
	}
	const axis = dir === 'left' || dir === 'right' ? 'across' : 'down';
	const cell = model.answers[cur.answerId]!.cells[cur.index]!;

	if (model.answers[cur.answerId]!.axis !== axis) {
		const crossing = (model.answersByCell[cell] ?? []).find((id) => model.answers[id]!.axis === axis);
		if (crossing) return endEntry({ ...state, cursor: at(model, crossing, cell) });
	}

	const [dr, dc] = STEP[dir];
	let row = Math.floor(cell / model.width) + dr;
	let col = (cell % model.width) + dc;
	while (row >= 0 && row < model.height && col >= 0 && col < model.width) {
		const target = row * model.width + col;
		if (model.kinds[target] === 'letter') {
			const covering = model.answersByCell[target] ?? [];
			const pick = covering.find((id) => model.answers[id]!.axis === axis) ?? covering[0];
			return pick ? endEntry({ ...state, cursor: at(model, pick, target) }) : state;
		}
		row += dr;
		col += dc;
	}
	return state;
}

/**
 * Tab / Shift+Tab: the next (or previous) *unsolved* answer in `answerOrder`, wrapping,
 * with the cursor on its first empty cell. No change when everything is solved.
 */
export function nextAnswer(model: PuzzleModel, state: GameState, delta: 1 | -1): GameState {
	const order = model.answerOrder;
	const n = order.length;
	if (n === 0) return state;
	const curPos = state.cursor ? order.indexOf(state.cursor.answerId) : -1;
	const start = curPos !== -1 ? curPos : delta === 1 ? -1 : n;
	for (let i = 1; i <= n; i++) {
		const id = order[(((start + delta * i) % n) + n) % n]!;
		if (!state.solvedAnswers.includes(id)) return selectAnswer(model, state, id);
	}
	return state;
}

/**
 * Where to go after `solvedId` is solved: selects `rankNextAnswers`'s choice (Flow
 * navigation, see `@arrow/game-engine/navigation`) with the cursor on its first empty
 * cell. A thin wrapper kept for callers that don't track Flow or the solve's `opened`
 * list; the UI calls `rankNextAnswers` directly to get the scored decision too. No
 * change when everything is solved.
 */
export function nextAnswerAfterSolve(model: PuzzleModel, state: GameState, solvedId: string): GameState {
	const { selected } = rankNextAnswers(model, state, { solvedId });
	if (selected === null) return state;
	const next = selectAnswer(model, state, selected);
	return state.echo ? { ...next, echo: state.echo } : next;
}

/**
 * Switches to the crossing answer at the cursor cell, keeping the cursor on that cell.
 * No cursor, or no crossing answer there → the same state.
 */
export function toggleDirection(model: PuzzleModel, state: GameState): GameState {
	const cur = state.cursor;
	if (!cur) return state;
	const cell = model.answers[cur.answerId]?.cells[cur.index];
	if (cell === undefined) return state;
	const other = (model.answersByCell[cell] ?? []).find((id) => id !== cur.answerId);
	return other ? endEntry({ ...state, cursor: at(model, other, cell) }) : state;
}

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

/**
 * Writes letters, clears their flags, then detects newly solved answers and puzzle
 * completion. Shared by typing, reveal and solveAll.
 */
function applyFill(
	model: PuzzleModel,
	state: GameState,
	fills: Map<number, string>,
	now: number,
	source: SolveSource,
): Transition {
	const entries = [...state.entries];
	for (const [cell, letter] of fills) entries[cell] = letter;
	let next: GameState = {
		...endEntry(state),
		entries,
		flaggedCells: state.flaggedCells.filter((c) => !fills.has(c)),
	};

	const newlySolved = model.answerOrder.filter(
		(id) => !state.solvedAnswers.includes(id) && isCorrect(model, entries, model.answers[id]!),
	);
	const events: GameEvent[] = [];
	if (newlySolved.length > 0) {
		const solvedAnswers = [...state.solvedAnswers, ...newlySolved];
		// The pace: the active time at this solve, once per answer (several at once share it).
		const offset = Math.round(elapsedMs(state, now));
		const solveOffsets = [...(state.solveOffsets ?? []), ...newlySolved.map(() => offset)];
		next = { ...next, solvedAnswers, solveOffsets };
		const lockedNow = lockedCells(model, next);
		next.flaggedCells = next.flaggedCells.filter((c) => !lockedNow.has(c));
		for (const id of newlySolved) {
			const opened = model.crossings[id]!.filter((other) => !solvedAnswers.includes(other));
			events.push({ type: 'answerSolved', answerId: id, at: now, opened, source });
		}
	}

	if (next.completedAt === null && boardCorrect(model, entries)) {
		next = stopClock(next, now);
		events.push({ type: 'puzzleCompleted', at: now, elapsedMs: next.elapsedMs });
	}
	return { state: next, events };
}

/**
 * Types a letter at the cursor, with smart entry (docs/SMART-ENTRY.md): the letter
 * joins the entry session's buffer and the whole buffer is re-placed from the
 * session's snapshot, in gap mode or type-through mode (see `placeBuffer`). Non A–Z
 * input (after upper-casing) is ignored, as is typing with no cursor or on a completed
 * puzzle. Locked cells are never written; on a solved answer the cursor just advances.
 */
export function typeLetter(model: PuzzleModel, state: GameState, letter: string, now: number): Transition {
	const cur = state.cursor;
	if (!cur || state.completedAt !== null) return same(state);
	const upper = typeof letter === 'string' ? letter.toUpperCase() : '';
	if (!LETTER.test(upper)) return same(state);
	const answer = model.answers[cur.answerId];
	const cell = answer?.cells[cur.index];
	if (!answer || cell === undefined) return same(state);

	// The tail of a word that solved mid type-through: swallow the letters still coming.
	if (state.echo) {
		const { echo, ...rest } = state;
		if (echo.rest[0] === upper && now - echo.at <= ECHO_WINDOW_MS) {
			const left = echo.rest.slice(1);
			return { state: left ? { ...rest, echo: { ...echo, rest: left, at: now } } : rest, events: [] };
		}
		state = rest;
	}

	const locked = lockedCells(model, state);
	const lockedNow = answer.cells.map((c) => locked.has(c));
	const current = answer.cells.map((c) => state.entries[c] ?? '');
	if (state.solvedAnswers.includes(answer.id)) {
		const index = advanceIndex(cur.index, current, lockedNow);
		return index === cur.index ? same(state) : same(endEntry({ ...state, cursor: { ...cur, index } }));
	}

	// Continue the session only if the cursor is still where it left it.
	const prior = state.entry;
	const session =
		prior && prior.answerId === answer.id && prior.at === cur.index
			? prior
			: startSession(answer, cur.index, state.entries, locked);
	const buffer = session.buffer + upper;
	const placed = placeBuffer(session, buffer, current, lockedNow);
	const entry: EntrySession = { ...session, buffer, at: placed.cursor, mode: placed.mode };
	if (placed.from !== undefined) entry.from = placed.from;
	else delete entry.from;
	const cursor = { ...cur, index: placed.cursor };

	const fills = new Map<number, string>();
	answer.cells.forEach((c, i) => {
		if (!lockedNow[i] && placed.values[i] !== state.entries[c]) fills.set(c, placed.values[i]!);
	});
	if (fills.size === 0) return { state: { ...state, cursor, entry }, events: [] };

	const clocked = touchClock(state, now, true);
	const filled = applyFill(model, clocked, fills, now, 'typed');
	// Letter events in answer order, the cell the newest letter landed in last.
	const landed = placed.landed === undefined ? undefined : answer.cells[placed.landed];
	const changed = [...fills.keys()].sort((x, y) => Number(x === landed) - Number(y === landed));
	const letterEvents: GameEvent[] = changed.map((c) => {
		const value = fills.get(c)!;
		return value === '' ? { type: 'letterCleared', cell: c, at: now } : { type: 'letterEntered', cell: c, letter: value, at: now };
	});
	// Solved before the end of the word, when the buffer reads as the whole word typed
	// from its start: the player will still type the rest, so remember it briefly.
	let next: GameState = { ...filled.state, cursor, entry };
	const end = placed.wordEnd;
	if (end !== undefined && end > 0 && end < answer.cells.length && next.solvedAnswers.includes(answer.id)) {
		const rest = answer.cells.slice(end).map((c) => next.entries[c]).join('');
		next = { ...next, echo: { answerId: answer.id, rest, at: now } };
	}
	return { state: next, events: [...letterEvents, ...filled.events] };
}

/**
 * Backspace. If the cursor cell is editable and filled, clear it and stay. Otherwise
 * move back to the previous editable cell of the same answer and clear it (if it has a
 * letter). Never leaves the answer and never clears a locked cell.
 */
export function backspace(model: PuzzleModel, state: GameState, now: number): Transition {
	const cur = state.cursor;
	if (!cur || state.completedAt !== null) return same(state);
	const answer = model.answers[cur.answerId];
	if (!answer) return same(state);
	const locked = lockedCells(model, state);

	let index = cur.index;
	let cell = answer.cells[index]!;
	if (locked.has(cell) || state.entries[cell] === '') {
		let prev = index - 1;
		while (prev >= 0 && locked.has(answer.cells[prev]!)) prev--;
		if (prev < 0) return same(state);
		index = prev;
		cell = answer.cells[index]!;
	}

	const moved: GameState = endEntry({ ...state, cursor: { ...cur, index } });
	if (moved.entries[cell] === '') return same(moved);
	const entries = [...moved.entries];
	entries[cell] = '';
	return {
		state: {
			...touchClock(moved, now, false),
			entries,
			flaggedCells: moved.flaggedCells.filter((c) => c !== cell),
		},
		events: [{ type: 'letterCleared', cell, at: now }],
	};
}

// ---------------------------------------------------------------------------
// Help: check & reveal
// ---------------------------------------------------------------------------

function flag(model: PuzzleModel, state: GameState, cells: number[], now: number): Transition {
	const locked = lockedCells(model, state);
	const wrong = cells.filter(
		(c) => !locked.has(c) && state.entries[c] !== '' && state.entries[c] !== model.solution[c],
	);
	const flaggedCells = [...new Set([...state.flaggedCells, ...wrong])].sort((a, b) => a - b);
	const newlyFlagged = wrong.filter((c) => !state.flaggedCells.includes(c)).length;
	const mistakes = (state.mistakes ?? 0) + newlyFlagged;
	const flagged: GameState = { ...state, flaggedCells, checksUsed: state.checksUsed + 1, mistakes };
	if (wrong.length > 0) flagged.everFlagged = [...new Set([...(state.everFlagged ?? []), ...wrong])].sort((a, b) => a - b);
	return {
		state: endEntry(flagged),
		events: [{ type: 'cellsFlagged', cells: wrong, at: now }],
	};
}

/**
 * Flags the current answer's filled, wrong cells. Empty cells are never flagged.
 * Counts as a check (`checksUsed + 1`) and always emits `cellsFlagged` (possibly with no
 * cells, meaning "nothing wrong so far"). No-op with no cursor, on a solved answer, or
 * once the puzzle is complete.
 */
export function checkAnswer(model: PuzzleModel, state: GameState, now: number): Transition {
	const cur = state.cursor;
	if (!cur || state.completedAt !== null || state.solvedAnswers.includes(cur.answerId)) return same(state);
	const answer = model.answers[cur.answerId];
	if (!answer) return same(state);
	return flag(model, state, answer.cells, now);
}

/** Like `checkAnswer` for the whole grid. No-op once complete. */
export function checkPuzzle(model: PuzzleModel, state: GameState, now: number): Transition {
	if (state.completedAt !== null) return same(state);
	return flag(model, state, model.letterCells, now);
}

/**
 * Fills the current answer correctly. It counts as solved (events carry
 * `source: 'reveal'`, which Flow ignores) and increments `revealsUsed`. No-op with no
 * cursor, on an already solved answer, or once complete. Starts the clock if needed.
 */
export function revealAnswer(model: PuzzleModel, state: GameState, now: number): Transition {
	const cur = state.cursor;
	if (!cur || state.completedAt !== null || state.solvedAnswers.includes(cur.answerId)) return same(state);
	const answer = model.answers[cur.answerId];
	if (!answer) return same(state);
	const fills = new Map<number, string>();
	for (const c of answer.cells) if (state.entries[c] !== model.solution[c]) fills.set(c, model.solution[c]!);

	const clocked = {
		...touchClock(state, now, true),
		revealsUsed: state.revealsUsed + 1,
		revealedAnswers: [...(state.revealedAnswers ?? []), answer.id],
	};
	const filled = applyFill(model, clocked, fills, now, 'reveal');
	const letterEvents: GameEvent[] = [...fills].map(([cell, letter]) => ({
		type: 'letterEntered',
		cell,
		letter,
		at: now,
	}));
	return { state: filled.state, events: [...letterEvents, ...filled.events] };
}

/**
 * Dev tooling only: fills every cell, then emits `answerSolved` (in answer order,
 * `source: 'solveAll'`) and `puzzleCompleted`. No `letterEntered` events.
 */
export function solveAll(model: PuzzleModel, state: GameState, now: number): Transition {
	if (state.completedAt !== null) return same(state);
	const fills = new Map<number, string>();
	for (const c of model.letterCells) if (state.entries[c] !== model.solution[c]) fills.set(c, model.solution[c]!);
	return applyFill(model, touchClock(state, now, true), fills, now, 'solveAll');
}
